import { motion } from 'motion/react';
import { useCallback, useState } from 'react';
import { selectRoom } from '@/entities/room';
import { formatCountdown, useCountdown } from '@/shared/hooks/use-countdown';
import { useGameStore, useSessionStore } from '@/shared/hooks/use-shallow-store';
import { type UiEventMap, useUiEvent } from '@/shared/lib/events';
import { useT } from '@/shared/lib/i18n';

const VISIBLE_FOR_MS = 4_000;

/** Surfaces the most recent `HostChanged` server message for ~4s. */
export const HostChangedBanner = () => {
  const t = useT();
  const room = useGameStore(selectRoom);
  const identity = useSessionStore((s) => s.identity);
  const [lastChange, setLastChange] = useState<UiEventMap['host-changed'] | null>(null);
  // Replay-on-mount keeps the banner visible if a phase navigation remounts mid-window.
  useUiEvent(
    'host-changed',
    useCallback((payload) => setLastChange(payload), []),
    { replay: true },
  );

  const expiresAt = lastChange ? lastChange.at + VISIBLE_FOR_MS : undefined;
  const remaining = useCountdown(expiresAt);

  if (!lastChange || !room || !identity) return null;
  if (remaining !== null && remaining <= 0) return null;

  const target = room.players.find((p) => p.id === lastChange.newHostId);
  if (!target) return null;

  const isMe = target.id === identity.playerId;
  const message = isMe
    ? t('online.host_changed.you_banner')
    : t('online.host_changed.banner', { name: target.name });

  return (
    <motion.div
      key={`${lastChange.newHostId}-${lastChange.at}`}
      initial={{ opacity: 0, y: -6, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.28, ease: [0.5, 1.5, 0.5, 1] }}
      className="mx-3 mt-2 flex items-center gap-2 rounded-md border-2 border-ink bg-pop px-3 py-1.5 font-display font-extrabold text-[12px] text-ink shadow-[3px_3px_0_var(--color-ink)]"
      aria-live="polite"
    >
      <span
        aria-hidden
        className="rounded-sm border-[1.5px] border-ink bg-cream px-1.5 py-0.5 font-extrabold text-[8.5px] uppercase tracking-[0.16em]"
      >
        HOST
      </span>
      <span className="flex-1 truncate">{message}</span>
      {remaining !== null && (
        <span className="font-mono text-[10px] text-ink/60 tracking-[0.1em]">
          {formatCountdown(remaining)}
        </span>
      )}
    </motion.div>
  );
};
